import qs, { ParsedQuery } from 'query-string';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { setLocalStorage } from 'helpers/localStorage';
import { sessionKey } from './consts';

/**
 * Хук для сохранения и восстановления параметров фильтров в `localStorage`.
 * Используется для восстановления состояния фильтрации после навигации/перезагрузки.
 *
 * @param storageName - ключ, под которым будут храниться параметры фильтра
 * @returns Методы для получения сохранённых значений и «сырых» параметров
 */
export const useFilterParams = (storageName: string) => {
  const location = useLocation();

  const getFilterValues = useCallback((): ParsedQuery | null => {
    const storage = localStorage.getItem(sessionKey);

    if (storage?.[storageName]) {
      return qs.parse(storage[storageName]);
    }

    return null;
  }, [storageName]);

  const getSavedParams = useCallback(() => {
    const storage = localStorage.getItem(sessionKey);
    const value = storage?.[storageName];

    return typeof value === 'string' ? value : null;
  }, [storageName]);

  useEffect(
    () => () => {
      setLocalStorage(storageName, location.search);
    },
    [location, storageName],
  );

  // Для сохранения параметров фильтрации в localStorage при перезагрузке страницы
  useEffect(() => {
    const handleBeforeUnload = () => {
      setLocalStorage(storageName, location.search);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location, storageName]);

  return { getFilterValues, getSavedParams };
};
